/**
 * Map Slice
 * Manages map viewport and layer data
 */

import { createSlice } from '@reduxjs/toolkit';

const DEFAULT_VIEWPORT = {
  latitude: 39.5,        // Center of continental US
  longitude: -98.35,
  zoom: 4,               // Full US view before project selected
  bearing: 0,
  pitch: 0,
};

const initialState = {
  viewport: { ...DEFAULT_VIEWPORT },
  config: {
    mapboxApiAccessToken: import.meta.env.VITE_MAPBOX_TOKEN || '',
    mapStyle: 'mapbox://styles/mapbox/satellite-streets-v12',
  },
  currentMapId: null,
  
  // Viewport stack for drill-down navigation
  viewportStack: [],
  
  // Layer data
  assetsData: {},
  projectBoundariesData: {},
  overlaysData: {},
  movesData: {},
  
  // Overlay checkbox selections (shared between project and asset views)
  visibleOverlays: [],
  
  // Snapshot of project map state for save/restore during asset detail navigation
  savedProjectState: null,
  
  // Tooltip
  tooltip: null,
  
  // Layer visibility
  layerVisibility: {
    assets: true,
    projects: true,
    overlays: true,
    moves: true,
  },
};

const mapSlice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    initMap: () => initialState,
    
    clearMap: (state) => {
      state.assetsData = {};
      state.projectBoundariesData = {};
      state.overlaysData = {};
      state.movesData = {};
      state.tooltip = null;
    },
    
    setViewport: (state, action) => {
      state.viewport = action.payload;
    },
    
    updateViewport: (state, action) => {
      state.viewport = {
        ...state.viewport,
        ...action.payload,
      };
    },
    
    setCurrentMapId: (state, action) => {
      state.currentMapId = action.payload;
    },
    
    setMapConfig: (state, action) => {
      state.config = {
        ...state.config,
        ...action.payload,
      };
    },
    
    // Asset layer data
    setAssetsData: (state, action) => {
      state.assetsData = action.payload;
    },
    
    updateAssetData: (state, action) => {
      const { id, data } = action.payload;
      state.assetsData[id] = data;
    },
    
    removeAssetData: (state, action) => {
      delete state.assetsData[action.payload];
    },
    
    // Project boundaries
    setProjectBoundariesData: (state, action) => {
      state.projectBoundariesData = action.payload;
    },
    
    updateProjectBoundary: (state, action) => {
      const { id, data } = action.payload;
      state.projectBoundariesData[id] = data;
    },
    
    // Overlays
    setOverlaysData: (state, action) => {
      state.overlaysData = action.payload;
    },
    
    // Movement tracking
    setMovesData: (state, action) => {
      state.movesData = action.payload;
    },
    
    // Visible overlay selections
    setVisibleOverlays: (state, action) => {
      state.visibleOverlays = action.payload;
    },

    toggleOverlay: (state, action) => {
      const id = action.payload;
      if (state.visibleOverlays.includes(id)) {
        state.visibleOverlays = state.visibleOverlays.filter(o => o !== id);
      } else {
        state.visibleOverlays = [...state.visibleOverlays, id];
      }
    },

    // Save entire project map state before navigating to asset detail.
    // ASS-81 Part B fix: tagged with the projectId it belongs to, so a
    // caller can check identity before restoring -- without this, opening
    // project Y after X incorrectly inherited X's viewport/assets/boundary
    // data because the old snapshot carried no project identity at all.
    saveProjectMapState: (state, action) => {
      state.savedProjectState = {
        projectId: action.payload,
        assetsData: state.assetsData,
        projectBoundariesData: state.projectBoundariesData,
        overlaysData: state.overlaysData,
        viewport: { ...state.viewport },
        visibleOverlays: [...state.visibleOverlays],
      };
    },

    // Restore project map state when returning from asset detail
    restoreProjectMapState: (state) => {
      if (state.savedProjectState) {
        state.assetsData = state.savedProjectState.assetsData;
        state.projectBoundariesData = state.savedProjectState.projectBoundariesData;
        state.overlaysData = state.savedProjectState.overlaysData;
        state.viewport = state.savedProjectState.viewport;
        state.visibleOverlays = state.savedProjectState.visibleOverlays;
        state.savedProjectState = null;
      }
    },

    // Clear asset-detail-specific data (movesData) when leaving asset page
    clearAssetDetailState: (state) => {
      state.movesData = {};
    },

    // Clear saved project snapshot (when leaving project context entirely)
    clearSavedProjectState: (state) => {
      state.savedProjectState = null;
    },

    // Tooltip
    setTooltip: (state, action) => {
      state.tooltip = action.payload;
    },
    
    clearTooltip: (state) => {
      state.tooltip = null;
    },
    
    // Layer visibility
    setLayerVisibility: (state, action) => {
      const { layer, visible } = action.payload;
      state.layerVisibility[layer] = visible;
    },
    
    toggleLayerVisibility: (state, action) => {
      const layer = action.payload;
      state.layerVisibility[layer] = !state.layerVisibility[layer];
    },
    
    // Viewport stack for drill-down navigation
    pushViewportStack: (state, action) => {
      state.viewportStack.push({
        latitude: action.payload.latitude,
        longitude: action.payload.longitude,
        zoom: action.payload.zoom,
        bearing: action.payload.bearing || 0,
        pitch: action.payload.pitch || 0,
      });
    },
    
    popViewportStack: (state) => {
      if (state.viewportStack.length > 0) {
        const previousViewport = state.viewportStack.pop();
        state.viewport = {
          ...state.viewport,
          ...previousViewport,
        };
      }
    },
    
    clearViewportStack: (state) => {
      state.viewportStack = [];
    },
  },
});

export const {
  initMap,
  clearMap,
  setViewport,
  updateViewport,
  setCurrentMapId,
  setMapConfig,
  setAssetsData,
  updateAssetData,
  removeAssetData,
  setProjectBoundariesData,
  updateProjectBoundary,
  setOverlaysData,
  setMovesData,
  setVisibleOverlays,
  toggleOverlay,
  saveProjectMapState,
  restoreProjectMapState,
  clearAssetDetailState,
  clearSavedProjectState,
  setTooltip,
  clearTooltip,
  setLayerVisibility,
  toggleLayerVisibility,
  pushViewportStack,
  popViewportStack,
  clearViewportStack,
} = mapSlice.actions;

export default mapSlice.reducer;
